import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  Input,
} from '@angular/core';
import { ProductType } from '@core/models/api/dsm-types';
import { BillingFormService } from '@app/forms-store/services/billing-form.service';
import { Observable, Subject } from 'rxjs';
import { PriceSummaryDisplayContent } from '@app/hub/components/price-summary-display/price-summary-display-content';
import { QuoteLetterService } from '@core/services/quote-letter.service';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { ProductsService } from '@core/services/products.service';
import { LoadingService } from '@core/services/loading.service';
import { LayoutService } from '@core/services/layout.service';
import { Nullable } from '@shared/utils/type.utils';
import { CompositeActions } from '@core/store/actions';
import { Actions, ofType } from '@ngrx/effects';
import { RouterService } from '@core/services/router.service';
import { VersionedQuoteService } from '@core/store/entities/versioned-quote/versioned-quote.service';
import { PriceSummaryUtils } from '@shared/utils/hub/price-summary/price-summary.util';

@Component({
    selector: 'nwx-price-summary-container',
    templateUrl: './price-summary-container.component.html',
    styleUrls: ['./price-summary-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PriceSummaryContainerComponent implements OnDestroy {
  @Input() maxSizeMedium!: Nullable<boolean>;
  @Input() awaitingProtectorVersionChoice: Nullable<boolean>;

  maxSizeSmall$: Observable<boolean>;
  content$: Observable<PriceSummaryDisplayContent>;
  protectorContent$: Observable<PriceSummaryDisplayContent>;
  unsubscribe$ = new Subject<void>();

  constructor(
    private billingFormService: BillingFormService,
    private quoteLetterService: QuoteLetterService,
    private productsService: ProductsService,
    private loadingService: LoadingService,
    private layoutService: LayoutService,
    private actions$: Actions,
    private routerService: RouterService,
    private versionedQuoteService: VersionedQuoteService
  ) {
    this.content$ =
      this.billingFormService.getPriceSummaryDisplayContentInputs();
    this.protectorContent$ = this.versionedQuoteService
      .getProtectorQuoteResponses()
      .pipe(
        withLatestFrom(this.content$),
        map(([protectorQuoteResponses, content]) =>
          PriceSummaryUtils.getPriceSummaryDisplayContentFromProtectorResponses(
            protectorQuoteResponses,
            content
          )
        )
      );
    this.maxSizeSmall$ = this.layoutService.maxSizeSmall();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAddUpsell(productType: Nullable<ProductType>): void {
    if (productType === 'PersonalUmbrella') {
      const endLoading = this.loadingService.beginLoading(
        'add-umbrella',
        'Adding product...'
      );
      this.actions$
        .pipe(
          ofType(
            CompositeActions.submitPniPageSuccess,
            CompositeActions.submitPniPageError
          ),
          take(1)
        )
        .subscribe((action) => {
          if (action.type === CompositeActions.submitPniPageSuccess.type) {
            this.routerService.go({ path: ['quote', 'umbrella'] });
          }
          endLoading();
        });
      this.productsService.selectProduct('PersonalUmbrella', true);
      this.productsService.updateProductsAtHub().catch(() => {});
    }
  }

  onShowPdf(): void {
    this.quoteLetterService.showQuoteLetterInNewTab();
  }
}
